import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { AlertTriangle, Clock, RefreshCw, Mail, Loader2, Send, Eye, MousePointerClick, BellRing } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import PatientDetailDrawer from "./PatientDetailDrawer";

interface OverduePatient {
  assessmentId: string;
  userId: string;
  firstName: string | null;
  lastName: string | null;
  goal: string | null;
  approvedAt: string;
  daysSinceApproval: number;
  providerName: string | null;
  thresholdDays: number;
}

const OverdueConsultationWidget = () => {
  const [patients, setPatients] = useState<OverduePatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState<string | null>(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [runningSweep, setRunningSweep] = useState(false);
  const [lastReminderMap, setLastReminderMap] = useState<Record<string, string>>({});
  const [trackingMap, setTrackingMap] = useState<Record<string, { opens: number; clicks: number }>>({});
  const [lastAdminAlert, setLastAdminAlert] = useState<string | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);

    const { data: assessments } = await supabase
      .from("health_assessments")
      .select("id, user_id, goal, updated_at, status")
      .in("status", ["approved", "reviewed"]);

    if (!assessments || assessments.length === 0) {
      setPatients([]);
      setLoading(false);
      return;
    }

    const patientIds = assessments.map((a) => a.user_id);

    const { data: appointments } = await supabase
      .from("appointments")
      .select("patient_id, status")
      .in("patient_id", patientIds)
      .in("status", ["scheduled", "confirmed", "completed"]);

    const patientsWithAppts = new Set(
      (appointments || []).map((a) => a.patient_id)
    );

    const overdueAssessments = assessments.filter(
      (a) => !patientsWithAppts.has(a.user_id)
    );

    if (overdueAssessments.length === 0) {
      setPatients([]);
      setLoading(false);
      return;
    }

    const overdueUserIds = overdueAssessments.map((a) => a.user_id);

    const { data: profiles } = await supabase
      .from("profiles")
      .select("user_id, first_name, last_name, assigned_provider_id")
      .in("user_id", overdueUserIds);

    const profileMap: Record<string, { first_name: string | null; last_name: string | null; assigned_provider_id: string | null }> = {};
    (profiles || []).forEach((p) => { profileMap[p.user_id] = p; });

    const providerIds = [...new Set(
      (profiles || []).map((p) => p.assigned_provider_id).filter(Boolean) as string[]
    )];
    const providerNameMap: Record<string, string> = {};
    const providerThresholdMap: Record<string, number> = {};
    if (providerIds.length > 0) {
      const { data: providerProfiles } = await supabase
        .from("profiles")
        .select("user_id, first_name, last_name, overdue_threshold_days")
        .in("user_id", providerIds);
      (providerProfiles || []).forEach((p) => {
        providerNameMap[p.user_id] = `Dr. ${p.last_name || p.first_name || "Unknown"}`;
        providerThresholdMap[p.user_id] = p.overdue_threshold_days ?? 3;
      });
    }

    // Fetch last reminder timestamps and message_ids for tracking
    const { data: reminderLogs } = await supabase
      .from("email_send_log")
      .select("recipient_email, created_at, message_id")
      .eq("template_name", "schedule-consultation-reminder")
      .eq("status", "sent")
      .order("created_at", { ascending: false });

    const messageIds: string[] = [];
    if (reminderLogs && reminderLogs.length > 0) {
      const { data: userEmails } = await supabase.rpc("get_user_emails");
      if (userEmails) {
        const emailToUser: Record<string, string> = {};
        (userEmails as any[]).forEach((e: any) => { emailToUser[e.email] = e.user_id; });
        const rMap: Record<string, string> = {};
        reminderLogs.forEach((log: any) => {
          const uid = emailToUser[log.recipient_email];
          if (uid && !rMap[uid]) rMap[uid] = log.created_at;
          if (log.message_id) messageIds.push(log.message_id);
        });
        setLastReminderMap(rMap);
      }
    }

    // Fetch tracking events for these message IDs
    if (messageIds.length > 0) {
      const { data: trackingEvents } = await supabase
        .from("email_tracking_events")
        .select("message_id, event_type")
        .in("message_id", messageIds.slice(0, 500));

      if (trackingEvents && trackingEvents.length > 0 && reminderLogs) {
        // Build message_id -> user_id mapping
        const { data: userEmails2 } = await supabase.rpc("get_user_emails");
        if (userEmails2) {
          const emailToUser: Record<string, string> = {};
          (userEmails2 as any[]).forEach((e: any) => { emailToUser[e.email] = e.user_id; });
          const midToUser: Record<string, string> = {};
          reminderLogs.forEach((log: any) => {
            if (log.message_id) {
              const uid = emailToUser[log.recipient_email];
              if (uid) midToUser[log.message_id] = uid;
            }
          });

          const tMap: Record<string, { opens: number; clicks: number }> = {};
          trackingEvents.forEach((ev: any) => {
            const uid = midToUser[ev.message_id];
            if (!uid) return;
            if (!tMap[uid]) tMap[uid] = { opens: 0, clicks: 0 };
            if (ev.event_type === "open") tMap[uid].opens++;
            if (ev.event_type === "click") tMap[uid].clicks++;
          });
          setTrackingMap(tMap);
        }
      } else {
        setTrackingMap({});
      }
    } else {
      setTrackingMap({});
    }

    const now = Date.now();
    const DEFAULT_THRESHOLD = 3;
    const result: OverduePatient[] = overdueAssessments.map((a) => {
      const profile = profileMap[a.user_id];
      const daysSince = Math.floor((now - new Date(a.updated_at).getTime()) / 86400000);
      const providerId = profile?.assigned_provider_id;
      const threshold = providerId ? (providerThresholdMap[providerId] ?? DEFAULT_THRESHOLD) : DEFAULT_THRESHOLD;
      return {
        assessmentId: a.id,
        userId: a.user_id,
        firstName: profile?.first_name || null,
        lastName: profile?.last_name || null,
        goal: a.goal,
        approvedAt: a.updated_at,
        daysSinceApproval: daysSince,
        providerName: providerId ? providerNameMap[providerId] || null : null,
        thresholdDays: threshold,
      };
    })
    .filter((p) => p.daysSinceApproval >= p.thresholdDays)
    .sort((a, b) => b.daysSinceApproval - a.daysSinceApproval);

    setPatients(result);

    // Fetch last admin overdue alert digest timestamp
    const { data: alertLogs } = await supabase
      .from("email_send_log")
      .select("created_at")
      .eq("template_name", "admin-overdue-consultation-alert")
      .eq("status", "sent")
      .order("created_at", { ascending: false })
      .limit(1);
    setLastAdminAlert(alertLogs && alertLogs.length > 0 ? alertLogs[0].created_at : null);

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const sendReminder = async (patient: OverduePatient) => {
    setSendingReminder(patient.userId);
    try {
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "schedule-consultation-reminder",
          recipientUserId: patient.userId,
          idempotencyKey: `admin-sched-reminder-${patient.assessmentId}-${Date.now()}`,
          notificationCategory: "appointment_reminders",
          templateData: {
            patientName: patient.firstName || undefined,
            providerName: patient.providerName || undefined,
            goal: patient.goal || undefined,
          },
        },
      });
      await supabase.from("notifications").insert({
        user_id: patient.userId,
        title: "Reminder: Schedule Your Consultation",
        message: "Your assessment has been approved! Please schedule your consultation to continue with your treatment plan.",
        type: "appointment_reminder",
        related_id: patient.assessmentId,
      });
      setLastReminderMap((prev) => ({ ...prev, [patient.userId]: new Date().toISOString() }));
      toast({ title: "Reminder sent", description: `Scheduling reminder sent to ${patient.firstName || "patient"}.` });
    } catch (err) {
      console.error("Failed to send reminder:", err);
      toast({ title: "Error", description: "Failed to send reminder.", variant: "destructive" });
    } finally {
      setSendingReminder(null);
    }
  };

  const sendAllReminders = async () => {
    if (patients.length === 0) return;
    setSendingAll(true);
    let sent = 0;
    let failed = 0;
    for (const patient of patients) {
      try {
        await supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "schedule-consultation-reminder",
            recipientUserId: patient.userId,
            idempotencyKey: `admin-sched-reminder-${patient.assessmentId}-${Date.now()}`,
            notificationCategory: "appointment_reminders",
            templateData: {
              patientName: patient.firstName || undefined,
              providerName: patient.providerName || undefined,
              goal: patient.goal || undefined,
            },
          },
        });
        await supabase.from("notifications").insert({
          user_id: patient.userId,
          title: "Reminder: Schedule Your Consultation",
          message: "Your assessment has been approved! Please schedule your consultation to continue with your treatment plan.",
          type: "appointment_reminder",
          related_id: patient.assessmentId,
        });
        setLastReminderMap((prev) => ({ ...prev, [patient.userId]: new Date().toISOString() }));
        sent++;
      } catch {
        failed++;
      }
    }
    setSendingAll(false);
    const desc = failed > 0
      ? `Sent ${sent} reminder${sent !== 1 ? "s" : ""}, ${failed} failed.`
      : `Sent ${sent} reminder${sent !== 1 ? "s" : ""} successfully.`;
    toast({ title: "Bulk reminders complete", description: desc, variant: failed > 0 ? "destructive" : "default" });
  };

  const urgencyBadge = (days: number, threshold: number) => {
    const overdueDays = days - threshold;
    if (overdueDays >= 7) return <Badge variant="destructive" className="text-[10px]">{days}d ({overdueDays}d over)</Badge>;
    if (overdueDays >= 3) return <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">{days}d ({overdueDays}d over)</Badge>;
    return <Badge variant="secondary" className="text-[10px]">{days}d (just overdue)</Badge>;
  };

  const formatReminderTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
      " at " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle size={15} className="text-amber-500" />
              Overdue Consultation Bookings
              {patients.length > 0 && (
                <Badge variant="destructive" className="text-[10px] ml-1">{patients.length}</Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1.5"
                onClick={async () => {
                  setRunningSweep(true);
                  try {
                    const { data, error } = await supabase.functions.invoke("check-consultation-reminders", { body: {} });
                    if (error) throw error;
                    const sent = data?.sent ?? 0;
                    const adminAlertSent = data?.adminAlertSent ?? false;
                    toast({
                      title: sent > 0
                        ? `Sent ${sent} reminder${sent === 1 ? "" : "s"}`
                        : "No reminders sent",
                      description: sent > 0
                        ? `Patients overdue ≥3 days were emailed${adminAlertSent ? " and admin digest was triggered" : ""}.`
                        : "No patients hit the 3-day overdue threshold (or all already reminded in last 3 days).",
                    });
                    load();
                  } catch (e) {
                    toast({
                      title: "Reminder sweep failed",
                      description: e instanceof Error ? e.message : String(e),
                      variant: "destructive",
                    });
                  } finally {
                    setRunningSweep(false);
                  }
                }}
                disabled={runningSweep || loading}
                title="Run check-consultation-reminders now (same logic as the daily cron)"
              >
                {runningSweep ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />}
                {runningSweep ? "Running…" : "Run reminder sweep"}
              </Button>
              {patients.length > 1 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1.5"
                      disabled={sendingAll || loading}
                    >
                      {sendingAll ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Send size={12} />
                      )}
                      {sendingAll ? "Sending…" : `Remind All (${patients.length})`}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Send reminders to all overdue patients?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will send a scheduling reminder email to {patients.length} patient{patients.length !== 1 ? "s" : ""} who haven't booked a consultation yet. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={sendAllReminders}>
                        Send {patients.length} Reminder{patients.length !== 1 ? "s" : ""}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={load} disabled={loading}>
                <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Patients with approved assessments who haven't booked a consultation yet
            </p>
            {lastAdminAlert && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1 shrink-0 ml-2">
                    <Mail size={10} />
                    Digest: {formatReminderTime(lastAdminAlert)}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Last automated admin overdue alert digest sent
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : patients.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">
              🎉 All approved patients have booked consultations
            </p>
          ) : (
            <div className="max-h-[320px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Patient</TableHead>
                    <TableHead className="text-xs">Goal</TableHead>
                    <TableHead className="text-xs">Provider</TableHead>
                    <TableHead className="text-xs">Approved</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patients.map((p) => (
                    <TableRow key={p.assessmentId} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedPatientId(p.userId)}>
                      <TableCell className="text-sm font-medium">
                        {p.firstName || p.lastName
                          ? `${p.firstName || ""} ${p.lastName || ""}`.trim()
                          : "Unknown"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {p.goal || "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {p.providerName || "Unassigned"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(p.approvedAt), { addSuffix: true })}
                      </TableCell>
                      <TableCell>{urgencyBadge(p.daysSinceApproval, p.thresholdDays)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-col items-end gap-0.5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-1.5 h-7 text-xs"
                                disabled={sendingReminder === p.userId}
                                onClick={() => sendReminder(p)}
                              >
                                {sendingReminder === p.userId ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <Mail size={12} />
                                )}
                                {sendingReminder === p.userId ? "Sending…" : "Remind"}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="text-xs">
                              Send scheduling reminder email
                            </TooltipContent>
                          </Tooltip>
                          {lastReminderMap[p.userId] && (
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-muted-foreground">
                                Last: {formatReminderTime(lastReminderMap[p.userId])}
                              </span>
                              {trackingMap[p.userId] && (
                                <div className="flex items-center gap-1.5">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className={`flex items-center gap-0.5 text-[10px] ${trackingMap[p.userId].opens > 0 ? "text-green-600" : "text-muted-foreground"}`}>
                                        <Eye size={10} />
                                        {trackingMap[p.userId].opens}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="text-xs">
                                      Email opened {trackingMap[p.userId].opens} time{trackingMap[p.userId].opens !== 1 ? "s" : ""}
                                    </TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className={`flex items-center gap-0.5 text-[10px] ${trackingMap[p.userId].clicks > 0 ? "text-blue-600" : "text-muted-foreground"}`}>
                                        <MousePointerClick size={10} />
                                        {trackingMap[p.userId].clicks}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="text-xs">
                                      Link clicked {trackingMap[p.userId].clicks} time{trackingMap[p.userId].clicks !== 1 ? "s" : ""}
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      <PatientDetailDrawer patientId={selectedPatientId} onClose={() => setSelectedPatientId(null)} />
    </TooltipProvider>
  );
};

export default OverdueConsultationWidget;